import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { matchesSearch } from '@/utils/searchUtils';
import { Plus, GraduationCap, Calendar, AlertCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import ChildAvatar from '@/components/ChildAvatar';

const Enrollments = () => {
  const [children, setChildren] = useState([]);
  const [allChildren, setAllChildren] = useState([]);
  const [filteredChildren, setFilteredChildren] = useState([]);
  const [activities, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [allowRepeatPerActivity, setAllowRepeatPerActivity] = useState({});
  const [slotInfo, setSlotInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [viewingSchedule, setViewingSchedule] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [childSearchTerm, setChildSearchTerm] = useState('');
  const [capacityWarnings, setCapacityWarnings] = useState([]);
  const [showCapacityDialog, setShowCapacityDialog] = useState(false);
  const [childrenSlotInfo, setChildrenSlotInfo] = useState({}); // {child_id: slotInfo}
  const [showOnlyIncomplete, setShowOnlyIncomplete] = useState(false);
  
  // Estados para opções de slot na grade
  const [slotOptions, setSlotOptions] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotOptionsLoading, setSlotOptionsLoading] = useState(false);
  
  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    origin: '',
    days: '',
    shift: '',
    sortBy: 'name',
    sortOrder: 'asc'
  });

  useEffect(() => {
    fetchData();
  }, [showInactive]);

  useEffect(() => {
    applyFilters();
  }, [filters, allChildren, showOnlyIncomplete, childrenSlotInfo]);

  // Atualizar slots quando a página ganha foco (voltando da aba Grupos)
  useEffect(() => {
    const handleFocus = () => {
      if (allChildren.length > 0) {
        fetchAllChildrenSlotInfo();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [allChildren]);

  const refreshSlotInfo = () => {
    fetchAllChildrenSlotInfo();
    toast.success('Informações de slots atualizadas');
  };

  const fetchData = async () => {
    try {
      const [childrenRes, activitiesRes] = await Promise.all([
        axios.get(`${API}/children?active_only=false`),
        axios.get(`${API}/activities`)
      ]);
      
      let childrenData = childrenRes.data;
      
      // Filtrar por status ativo/inativo
      if (showInactive) {
        childrenData = childrenData.filter(c => !c.is_active);
      } else {
        childrenData = childrenData.filter(c => c.is_active);
      }
      
      setAllChildren(childrenData);
      setChildren(childrenData);
      setCourses(activitiesRes.data.filter(a => a.type !== 'outro'));
      
      // Buscar info de slots para todas as crianças ativas
      fetchAllChildrenSlotInfo();
      
      applyFilters(childrenData);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllChildrenSlotInfo = async () => {
    try {
      const res = await axios.get(`${API}/slot-info-batch`);
      setChildrenSlotInfo(res.data || {});
    } catch (error) {
      console.error('Erro ao buscar slots:', error);
    }
  };

  const applyFilters = (data = allChildren) => {
    let filtered = [...data];

    // Filtro por slots vazios
    if (showOnlyIncomplete) {
      filtered = filtered.filter(c => {
        const info = childrenSlotInfo[c.id];
        return info && info.vacant_slots > 0;
      });
    }

    // Busca por nome
    if (filters.search) {
      filtered = filtered.filter(c => 
        matchesSearch(c.name, filters.search) ||
        matchesSearch(c.guardian_name, filters.search) ||
        (c.enrollment_number && c.enrollment_number.includes(filters.search))
      );
    }

    // Filtro por origem
    if (filters.origin) {
      filtered = filtered.filter(c => c.enrollment_origin === filters.origin);
    }

    // Filtro por turno
    if (filters.shift) {
      filtered = filtered.filter(c => c.shift === filters.shift);
    }

    // Filtro por dias
    if (filters.days) {
      if (filters.days === 'ter_qui') {
        filtered = filtered.filter(c => 
          c.days_of_week && c.days_of_week.includes('terca') && c.days_of_week.includes('quinta')
        );
      } else if (filters.days === 'qua_sex') {
        filtered = filtered.filter(c => 
          c.days_of_week && c.days_of_week.includes('quarta') && c.days_of_week.includes('sexta')
        );
      } else if (filters.days === 'todos') {
        filtered = filtered.filter(c => 
          c.days_of_week && 
          c.days_of_week.includes('segunda') &&
          c.days_of_week.includes('terca') &&
          c.days_of_week.includes('quarta') &&
          c.days_of_week.includes('quinta') &&
          c.days_of_week.includes('sexta')
        );
      }
    }

    // Ordenação
    filtered.sort((a, b) => {
      if (filters.sortBy === 'name') {
        if (filters.sortOrder === 'asc') {
          return a.name.localeCompare(b.name);
        } else {
          return b.name.localeCompare(a.name);
        }
      } else if (filters.sortBy === 'age') {
        if (filters.sortOrder === 'asc') {
          return a.age - b.age;
        } else {
          return b.age - a.age;
        }
      }
      return 0;
    });

    setFilteredChildren(filtered);
  };

  const handleChildSelect = async (childId) => {
    setSelectedChild(childId);
    setSelectedCourses([]);
    setSlotInfo(null);
    
    // Filter activities based on child's shift and days
    const child = allChildren.find(c => c.id === childId);
    if (child) {
      const childShift = child.shift; // Matutino ou Vespertino
      const childDays = child.days_of_week || [];
      
      const compatible = activities.filter(activity => {
        // Se a atividade tem o novo formato 'schedule'
        if (activity.schedule && Object.keys(activity.schedule).length > 0) {
          // Verificar se pelo menos um dia da criança está no schedule da atividade
          // e se o turno da criança está disponível nesse dia
          return childDays.some(day => {
            const shiftsForDay = activity.schedule[day];
            return shiftsForDay && shiftsForDay.includes(childShift);
          });
        }
        
        // Formato antigo: verificar shifts array ou shift único
        const activityShifts = activity.shifts && activity.shifts.length > 0 
          ? activity.shifts 
          : (activity.shift ? [activity.shift] : []);
        
        // Verificar se o turno da criança está nos turnos da atividade
        if (!activityShifts.includes(childShift)) return false;
        
        // Verificar se pelo menos um dia coincide
        const activityDays = activity.days_of_week || [];
        return activityDays.some(day => childDays.includes(day));
      });
      
      setFilteredCourses(compatible);
      
      // Buscar informações de slots da criança
      try {
        const response = await axios.get(`${API}/children/${childId}/slot-info`);
        setSlotInfo(response.data);
      } catch (error) {
        console.error('Erro ao buscar info de slots:', error);
      }
    }
  };

  const handleSubmit = async (e, allowExceed = false) => {
    if (e) e.preventDefault();
    if (!selectedChild || selectedCourses.length === 0) {
      toast.error('Selecione uma criança e pelo menos uma atividade');
      return;
    }
    try {
      const child = allChildren.find(c => c.id === selectedChild);
      const frequency = child?.days_of_week?.length || 5;
      
      // Verificar se alguma atividade tem repetição marcada
      const hasAnyRepeat = selectedCourses.some(id => allowRepeatPerActivity[id]);
      
      const response = await axios.post(`${API}/enrollments`, {
        child_id: selectedChild,
        activities: selectedCourses.map(activityId => ({
          activity_id: activityId,
          frequency: frequency,
          allow_repeat: allowRepeatPerActivity[activityId] || false
        })),
        replace_existing: true,  // SEMPRE substituir automaticamente
        fill_all_slots: true,
        allow_global_repeat: hasAnyRepeat,
        allow_exceed_capacity: allowExceed
      });
      
      // Verificar se precisa confirmação de capacidade
      if (response.data.requires_confirmation) {
        setCapacityWarnings(response.data.capacity_warnings || []);
        setShowCapacityDialog(true);
        return;
      }
      
      toast.success('Grade preenchida com sucesso!');
      setDialogOpen(false);
      setShowCapacityDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao realizar matrículas');
    }
  };
  
  const handleConfirmExceedCapacity = async () => {
    await handleSubmit(null, true);
  };

  const resetForm = () => {
    setSelectedChild(null);
    setSelectedCourses([]);
    setFilteredCourses([]);
    setChildSearchTerm('');
    setAllowRepeatPerActivity({});
    setSlotInfo(null);
    setCapacityWarnings([]);
    setShowCapacityDialog(false);
  };

  // Função para abrir o modal de matrícula com criança pré-selecionada
  const openEnrollmentForChild = (child) => {
    handleChildSelect(child.id);
    setChildSearchTerm(child.name);
    setDialogOpen(true);
  };

  const toggleCourse = (activityId) => {
    setSelectedCourses(prev =>
      prev.includes(activityId) ? prev.filter(id => id !== activityId) : [...prev, activityId]
    );
  };

  const viewSchedule = async (child) => {
    try {
      const response = await axios.get(`${API}/children/${child.id}/schedule`);
      setViewingSchedule({ child, schedule: response.data });
      setSlotOptions(null);
      setSelectedSlot(null);
      setScheduleDialogOpen(true);
    } catch (error) {
      console.error('Erro ao carregar grade:', error);
      toast.error(error.response?.data?.detail || 'Erro ao carregar grade de horários');
    }
  };

  const fetchSlotOptions = async (child, day, slot) => {
    setSlotOptionsLoading(true);
    try {
      const response = await axios.get(`${API}/enrollments/slot-options/${child.id}/${day}/${slot}`);
      setSlotOptions(response.data);
      setSelectedSlot({ day, slot });
    } catch (error) {
      toast.error('Erro ao carregar opções');
    } finally {
      setSlotOptionsLoading(false);
    }
  };

  const handleSlotClick = (child, day, slot) => {
    // Só permite clicar se for um dia da criança
    const childDays = child.days_of_week || [];
    if (childDays.includes(day)) {
      fetchSlotOptions(child, day, slot);
    }
  };

  const handleSelectSlotOption = async (option) => {
    if (!viewingSchedule || !selectedSlot) return;
    const child = viewingSchedule.child;
    
    try {
      // Usar endpoint específico para slot
      await axios.post(`${API}/enrollments/add-to-slot`, {
        child_id: child.id,
        activity_id: option.activity_id,
        day: selectedSlot.day,
        slot: selectedSlot.slot
      });
      
      toast.success(`Matriculado em ${option.activity_name}`);
      
      // Recarregar grade e dados
      setSlotOptions(null);
      setSelectedSlot(null);
      const response = await axios.get(`${API}/children/${child.id}/schedule`);
      setViewingSchedule({ child, schedule: response.data });
      fetchAllChildrenSlotInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao matricular');
    }
  };

  const getDayLabel = (day) => {
    const labels = {
      'segunda': 'Segunda',
      'terca': 'Terça',
      'quarta': 'Quarta',
      'quinta': 'Quinta',
      'sexta': 'Sexta'
    };
    return labels[day] || day;
  };

  const getTimeSlotLabel = (slot) => {
    return `${slot}° Horário`;
  };

  const renderSchedule = () => {
    if (!viewingSchedule) return null;
    const { child, schedule } = viewingSchedule;
    const days = ['segunda', 'terca', 'quarta', 'quinta', 'sexta'];
    const childDays = child.days_of_week || [];
    
    // Determinar qual turno mostrar baseado na criança
    const childShift = child.shift;
    const shiftKey = childShift === 'Matutino' ? 'Matutino' : 'Vespertino';
    const shiftLabel = childShift === 'Matutino' ? 'Manhã' : 'Tarde';

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">{shiftLabel}</h3>
          <p className="text-sm text-slate-500 mb-3">Clique em um slot para ver opções de atividades</p>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-slate-200">
              <thead>
                <tr className="bg-slate-50">
                  <th className="border border-slate-200 px-4 py-2 text-left text-sm font-medium text-slate-600">
                    Horário
                  </th>
                  {days.map(day => (
                    <th key={day} className="border border-slate-200 px-4 py-2 text-center text-sm font-medium text-slate-600">
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3].map(slot => (
                  <tr key={slot}>
                    <td className="border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700">
                      {childShift === 'Matutino'
                        ? slot === 1 ? '8h45-9h30' : slot === 2 ? '9h30-10h15' : '10h15-11h00'
                        : slot === 1 ? '14h00-14h45' : slot === 2 ? '14h45-15h30' : '15h30-16h15'}
                    </td>
                    {days.map(day => {
                      const hasActivity = schedule[shiftKey] && schedule[shiftKey][day] && schedule[shiftKey][day][slot - 1];
                      const isChildDay = childDays.includes(day);
                      const isSelected = selectedSlot?.day === day && selectedSlot?.slot === slot;
                      
                      return (
                        <td 
                          key={day} 
                          className={`border border-slate-200 px-4 py-2 text-center text-sm transition-all ${
                            isSelected ? 'ring-2 ring-[#D87A4E] bg-[#D87A4E]/20' :
                            hasActivity ? 'bg-[#D87A4E]/10 hover:bg-[#D87A4E]/20 cursor-pointer' : 
                            isChildDay ? 'bg-amber-50 hover:bg-amber-100 cursor-pointer' : 'bg-slate-50'
                          }`}
                          onClick={() => isChildDay && handleSlotClick(child, day, slot)}
                        >
                          {hasActivity ? (
                            <span className="inline-block bg-[#D87A4E]/20 text-[#D87A4E] px-2 py-1 rounded text-xs font-medium">
                              {schedule[shiftKey][day][slot - 1]}
                            </span>
                          ) : isChildDay ? (
                            <span className="text-amber-500 text-xs">vazio</span>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Loading de opções */}
        {slotOptionsLoading && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D87A4E]"></div>
          </div>
        )}
        
        {/* Opções de atividades para o slot selecionado */}
        {slotOptions && selectedSlot && (
          <div className="border rounded-lg p-4 bg-slate-50">
            <h4 className="font-medium text-slate-800 mb-2">
              Opções para {getDayLabel(selectedSlot.day)} - {getTimeSlotLabel(selectedSlot.slot)}
            </h4>
            {slotOptions.child_age && (
              <p className="text-xs text-slate-500 mb-3">
                {slotOptions.child_name} ({slotOptions.child_age} anos - {slotOptions.child_age_group})
              </p>
            )}
            {slotOptions.options && slotOptions.options.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                {slotOptions.options.map(option => (
                  <button
                    key={option.activity_id}
                    onClick={() => handleSelectSlotOption(option)}
                    className={`text-left p-3 rounded-lg border transition-all ${
                      option.is_full 
                        ? 'bg-amber-50 border-amber-200 hover:border-amber-400' 
                        : 'bg-white border-slate-200 hover:border-[#D87A4E]'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-slate-800">{option.activity_name}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        option.is_full ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {option.current_count}/{option.max_students}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-slate-500">
                        {option.activity_type} • {option.available > 0 ? `${option.available} vagas` : 'Turma cheia'}
                      </p>
                      {option.age_group && option.current_count > 0 && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          option.age_group === 'Faixa etária mista' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {option.age_group}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Nenhuma atividade disponível para este horário</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="enrollments-page">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Matrículas</h1>
            <p className="text-slate-600 mt-1">Gerencie matrículas com enturmação automática</p>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white p-4 rounded-lg border border-slate-200 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Busca por nome */}
            <div>
              <Label className="text-xs">Buscar por nome</Label>
              <Input
                placeholder="Digite o nome..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
              />
            </div>

            {/* Origem */}
            <div>
              <Label className="text-xs">Origem</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filters.origin}
                onChange={(e) => setFilters({...filters, origin: e.target.value})}
              >
                <option value="">Todas</option>
                <option value="livre_demanda">Livre Demanda</option>
                <option value="gov_celso_ramos">Governador Celso Ramos</option>
                <option value="biguacu">Biguaçu</option>
              </select>
            </div>

            {/* Turno */}
            <div>
              <Label className="text-xs">Turno</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filters.shift}
                onChange={(e) => setFilters({...filters, shift: e.target.value})}
              >
                <option value="">Todos</option>
                <option value="Matutino">Manhã</option>
                <option value="Vespertino">Tarde</option>
              </select>
            </div>

            {/* Dias */}
            <div>
              <Label className="text-xs">Dias de Participação</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filters.days}
                onChange={(e) => setFilters({...filters, days: e.target.value})}
              >
                <option value="">Todos</option>
                <option value="ter_qui">Ter e Qui</option>
                <option value="qua_sex">Qua e Sexta</option>
                <option value="todos">Todos os dias (Seg-Sex)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Ordenar Por */}
            <div>
              <Label className="text-xs">Ordenar Por</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filters.sortBy}
                onChange={(e) => setFilters({...filters, sortBy: e.target.value})}
              >
                <option value="name">Nome</option>
                <option value="age">Idade</option>
              </select>
            </div>

            {/* Ordem */}
            <div>
              <Label className="text-xs">Ordem</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filters.sortOrder}
                onChange={(e) => setFilters({...filters, sortOrder: e.target.value})}
              >
                <option value="asc">Crescente</option>
                <option value="desc">Decrescente</option>
              </select>
            </div>

            {/* Botão Limpar */}
            <div className="flex items-end">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setFilters({
                  search: '',
                  origin: '',
                  days: '',
                  shift: '',
                  sortBy: 'name',
                  sortOrder: 'asc'
                })}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>

          <div className="text-sm text-slate-600">
            Mostrando {filteredChildren.length} de {allChildren.length} criança(s)
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={showInactive ? 'default' : 'outline'}
            onClick={() => setShowInactive(!showInactive)}
            data-testid="toggle-inactive"
          >
            {showInactive ? 'Ativos' : 'Inativos'}
          </Button>
          <Button
            variant={showOnlyIncomplete ? 'default' : 'outline'}
            onClick={() => setShowOnlyIncomplete(!showOnlyIncomplete)}
            data-testid="toggle-incomplete"
            className={showOnlyIncomplete ? 'bg-amber-500 hover:bg-amber-600' : ''}
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            {showOnlyIncomplete ? 'Mostrando Incompletos' : 'Slots Vazios'}
            {!showOnlyIncomplete && Object.values(childrenSlotInfo).filter(i => i && i.vacant_slots > 0).length > 0 && (
              <span className="ml-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {Object.values(childrenSlotInfo).filter(i => i && i.vacant_slots > 0).length}
              </span>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={refreshSlotInfo}
            data-testid="refresh-slots"
            title="Atualizar informações de slots"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-enrollment-button">
              <Plus className="h-4 w-4 mr-2" />
              Nova Matrícula
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nova Matrícula</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="child_search">Selecione a Criança *</Label>
                <Input
                  id="child_search"
                  type="text"
                  placeholder="Digite o nome para buscar..."
                  className="mt-2 mb-2"
                  value={childSearchTerm}
                  onChange={(e) => setChildSearchTerm(e.target.value)}
                />
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-md">
                  {allChildren
                    .slice()
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .filter(child => 
                      !childSearchTerm || 
                      matchesSearch(child.name, childSearchTerm)
                    )
                    .map(child => (
                      <div
                        key={child.id}
                        onClick={() => {
                          handleChildSelect(child.id);
                          setChildSearchTerm(child.name);
                        }}
                        className={`px-4 py-3 cursor-pointer border-b border-slate-100 last:border-b-0 transition-colors ${
                          selectedChild === child.id
                            ? 'bg-[#D87A4E]/20 text-[#7A3D36] font-medium'
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="font-medium">{child.name}</div>
                        <div className="text-xs text-slate-500">
                          {child.age} anos • {child.shift === 'Matutino' ? 'Manhã' : 'Tarde'} • {child.enrollment_number || 'Sem matrícula'}
                        </div>
                      </div>
                    ))
                  }
                  {allChildren.filter(child => 
                    !childSearchTerm || 
                    (child.name || '').toLowerCase().startsWith(childSearchTerm.toLowerCase())
                  ).length === 0 && (
                    <div className="px-4 py-6 text-center text-slate-500">
                      Nenhuma criança encontrada
                    </div>
                  )}
                </div>
                {selectedChild && (
                  <div className="mt-3 p-3 bg-[#D87A4E]/10 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-[#7A3D36]">
                        Selecionado: {allChildren.find(c => c.id === selectedChild)?.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedChild(null);
                          setChildSearchTerm('');
                          setFilteredCourses([]);
                          setSelectedCourses([]);
                          setSlotInfo(null);
                        }}
                        className="text-xs text-[#7A3D36] hover:underline"
                      >
                        Limpar
                      </button>
                    </div>
                    {slotInfo && (
                      <div className="mt-2 text-xs text-[#7A3D36]/80">
                        <span className="font-medium">Slots:</span> {slotInfo.filled_slots}/{slotInfo.total_slots} preenchidos • 
                        <span className="font-semibold text-[#D87A4E]"> {slotInfo.vacant_slots} vago(s)</span>
                        {slotInfo.is_five_day_child && (
                          <span className="ml-1">(3 reservados para Integração)</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Label>Selecione os atividades * (Enturmação Automática)</Label>
                {selectedChild && (
                  <p className="text-xs text-slate-500 mb-2">
                    Mostrando apenas atividades compatíveis com o turno e dias da criança
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 max-h-96 overflow-y-auto">
                  {(selectedChild ? filteredCourses : activities).map(activity => {
                    // Calcular exibição de horários baseado no schedule ou formato antigo
                    const getScheduleDisplay = () => {
                      if (activity.schedule && Object.keys(activity.schedule).length > 0) {
                        const entries = Object.entries(activity.schedule)
                          .filter(([_, shifts]) => shifts && shifts.length > 0)
                          .map(([day, shifts]) => {
                            const dayLabel = day.charAt(0).toUpperCase() + day.slice(1, 3);
                            const shiftLabels = shifts.map(s => s === 'Matutino' ? 'M' : 'T').join('/');
                            return `${dayLabel}(${shiftLabels})`;
                          });
                        return entries.join(', ');
                      }
                      // Formato antigo
                      const shifts = activity.shifts?.length > 0 ? activity.shifts : (activity.shift ? [activity.shift] : []);
                      const shiftLabel = shifts.map(s => s === 'Matutino' ? 'M' : 'T').join('/');
                      const days = (activity.days_of_week || []).map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ');
                      return `${shiftLabel} - ${days}`;
                    };
                    
                    const isSelected = selectedCourses.includes(activity.id);
                    
                    return (
                      <div
                        key={activity.id}
                        data-testid={`activity-option-${activity.id}`}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          isSelected
                            ? 'border-[#D87A4E] bg-[#D87A4E]/10'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div 
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => toggleCourse(activity.id)}
                        >
                          <div>
                            <p className="font-semibold text-slate-800 text-sm">{activity.name}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {getScheduleDisplay()}
                            </p>
                          </div>
                          {isSelected && (
                            <div className="w-5 h-5 bg-[#D87A4E] rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">✓</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Checkbox de repetição - aparece quando atividade está selecionada */}
                        {isSelected && (
                          <div className="mt-3 pt-3 border-t border-[#D87A4E]/30">
                            <label 
                              className="flex items-center gap-2 cursor-pointer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={allowRepeatPerActivity[activity.id] || false}
                                onChange={(e) => {
                                  setAllowRepeatPerActivity(prev => ({
                                    ...prev,
                                    [activity.id]: e.target.checked
                                  }));
                                }}
                                className="w-4 h-4 text-[#D87A4E] rounded focus:ring-[#D87A4E]"
                              />
                              <span className="text-xs text-[#7A3D36] font-medium">
                                Repetir em 2 dias alternados (ex: Ter e Qui)
                              </span>
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Informações sobre preenchimento */}
              {slotInfo && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Preenchimento Automático:</strong> O sistema preencherá TODOS os {slotInfo.total_slots} horários da criança.
                    {slotInfo.is_five_day_child && (
                      <> Segunda-feira será preenchida com <strong>Integração</strong> (3 slots).</>
                    )}
                    <br />
                    <span className="text-blue-600">
                      Marque "Repetir atividade" para usar a mesma atividade em 2 dias alternados.
                    </span>
                  </p>
                </div>
              )}
              
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-enrollment-button" className="bg-[#D87A4E] hover:bg-[#c26a42]">
                  Matricular
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Schedule View Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={(open) => { 
        setScheduleDialogOpen(open); 
        if (!open) { 
          setSlotOptions(null); 
          setSelectedSlot(null); 
        } 
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Grade de Horários - {viewingSchedule?.child.name}
            </DialogTitle>
          </DialogHeader>
          {renderSchedule()}
        </DialogContent>
      </Dialog>

      {/* Children List with Schedule View */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="enrollments-list">
          {filteredChildren.map((child) => {
            const slotInfoChild = childrenSlotInfo[child.id];
            const hasVacantSlots = slotInfoChild && slotInfoChild.vacant_slots > 0;
            
            return (
            <div
              key={child.id}
              className={`bg-white rounded-xl shadow-md p-6 border card-hover ${
                hasVacantSlots ? 'border-amber-300' : 'border-slate-100'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <ChildAvatar 
                      photo={child.photo_url} 
                      name={child.name} 
                      size="lg"
                      zoomable={true}
                      className="border-2 border-blue-200"
                    />
                    {hasVacantSlots && (
                      <div 
                        className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center"
                        title={`${slotInfoChild.vacant_slots} slot(s) vazio(s)`}
                      >
                        <span className="text-white text-xs font-bold">{slotInfoChild.vacant_slots}</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{child.name}</h3>
                    <p className="text-sm text-slate-600">{child.age} anos</p>
                    {hasVacantSlots && (
                      <p className="text-xs text-amber-600 font-medium">
                        {slotInfoChild.filled_slots}/{slotInfoChild.total_slots} slots
                      </p>
                    )}
                  </div>
                </div>
                <GraduationCap className={`h-8 w-8 ${hasVacantSlots ? 'text-amber-500' : 'text-blue-500'}`} />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => viewSchedule(child)}
                  data-testid={`view-schedule-${child.id}`}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Grade
                </Button>
                <Button
                  size="sm"
                  className={`flex-1 ${hasVacantSlots ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#D87A4E] hover:bg-[#c26a42]'}`}
                  onClick={() => openEnrollmentForChild(child)}
                  data-testid={`enroll-child-${child.id}`}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Matricular
                </Button>
              </div>
            </div>
          )})}
        </div>
      )}

      {!loading && filteredChildren.length === 0 && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center border border-slate-100">
          <GraduationCap className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Nenhuma criança encontrada</p>
        </div>
      )}
      
      {/* Diálogo de Confirmação de Capacidade */}
      <Dialog open={showCapacityDialog} onOpenChange={setShowCapacityDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Atenção: Turmas com Capacidade Excedida
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              As seguintes turmas já estão cheias para a faixa etária desta criança:
            </p>
            <div className="max-h-60 overflow-y-auto space-y-2">
              {capacityWarnings.map((warning, idx) => (
                <div key={idx} className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <p className="font-medium text-amber-800">{warning.activity_name}</p>
                  <p className="text-amber-600">
                    {warning.day} - Slot {warning.slot} ({warning.age_group}): {warning.current_count}/{warning.max_students} vagas
                  </p>
                </div>
              ))}
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Sugestão:</strong> Marque "Repetir" em outra atividade que tenha vagas disponíveis, 
                ou confirme para exceder a capacidade desta turma.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowCapacityDialog(false)}
              >
                Voltar e Escolher Outras
              </Button>
              <Button 
                className="flex-1 bg-amber-500 hover:bg-amber-600"
                onClick={handleConfirmExceedCapacity}
              >
                Exceder Capacidade
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Enrollments;